import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { ArrowUpDown } from 'lucide-react'
import clsx from 'clsx'
import type { Loan } from '@/types'
import { useDealDossierStore } from '@/store/dealDossierStore'
import {
  compareLoans,
  defaultSortDirection,
  type LoanSortKey,
  sortLoans,
} from '@/utils/dealTableSort'

export function dealTableHeadPropsClass() {
  return 'deal-table-head-row'
}

/** Canonical name used by Command Center / Pipeline tables. */
export function dealTableHeadRowClass() {
  return dealTableHeadPropsClass()
}

/** @deprecated typo alias */
export const dealTableHeadPropClass = dealTableHeadPropsClass

export function dealTableRowClass(isFocused: boolean, extra?: string) {
  return clsx(
    'deal-table-row group border-b transition-all duration-150',
    isFocused ? 'deal-table-row--focused' : 'border-b-white/[0.04]',
    extra,
  )
}

/** Always open the live Deal Dossier — never the legacy curated Property Profile. */
function openDealSurface(dealId: string) {
  useDealDossierStore.getState().openDossier(dealId, 'overview')
}

export function useDealTableInteraction() {
  const [focusedDealId, setFocusedDealId] = useState<string | null>(null)

  const activateDeal = useCallback(
    (dealId: string, options?: { openProfile?: boolean; onActivate?: () => void }) => {
      setFocusedDealId(dealId)
      options?.onActivate?.()
      if ((options?.openProfile ?? true) && isLoanTableId(dealId)) {
        openDealSurface(dealId)
      }
    },
    [],
  )

  return { focusedDealId, setFocusedDealId, activateDeal }
}

export function useLoanTableSort(
  loans: Loan[],
  defaultKey: LoanSortKey = 'loanAmount',
  defaultDir?: 'asc' | 'desc',
) {
  const [sortKey, setSortKey] = useState<LoanSortKey>(defaultKey)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultDir ?? defaultSortDirection(defaultKey))

  const toggleSort = useCallback(
    (key: LoanSortKey) => {
      if (sortKey === key) {
        setSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'))
        return
      }
      setSortKey(key)
      setSortDir(defaultSortDirection(key))
    },
    [sortKey],
  )

  const sortedLoans = useMemo(() => sortLoans(loans, sortKey, sortDir), [loans, sortKey, sortDir])

  return { sortKey, sortDir, toggleSort, sortedLoans }
}

type DealTableSortHeaderProps = {
  label: string
  col: LoanSortKey
  sortKey: LoanSortKey
  sortDir: 'asc' | 'desc'
  onSort: (key: LoanSortKey) => void
  align?: 'left' | 'right' | 'center'
  className?: string
}

export function DealTableSortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  align = 'left',
  className,
}: DealTableSortHeaderProps) {
  return (
    <th
      className={clsx(
        'deal-table-sort-th font-mono text-[9px] px-1.5 py-1 cursor-pointer select-none transition-colors',
        sortKey === col ? 'text-amber-300' : 'text-stone-600 hover:text-stone-400',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
      onClick={() => onSort(col)}
    >
      <span
        className={clsx(
          'inline-flex items-center gap-0.5',
          align === 'right' && 'justify-end w-full',
          align === 'center' && 'justify-center w-full',
        )}
      >
        {label}
        {sortKey === col && (
          <ArrowUpDown size={8} className={clsx('text-amber-400', sortDir === 'asc' && 'rotate-180')} />
        )}
      </span>
    </th>
  )
}

type DealTableRowProps = {
  dealId: string
  isFocused: boolean
  onActivate?: (dealId: string) => void
  openProfileOnClick?: boolean
  className?: string
  children: ReactNode
}

export function DealTableRow({
  dealId,
  isFocused,
  onActivate,
  openProfileOnClick = true,
  className,
  children,
}: DealTableRowProps) {
  const handleActivate = () => {
    onActivate?.(dealId)
    if (openProfileOnClick && isLoanTableId(dealId)) {
      openDealSurface(dealId)
    }
  }

  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleActivate()
        }
      }}
      className={dealTableRowClass(isFocused, className)}
    >
      {children}
    </tr>
  )
}

type GenericSortHeaderProps = {
  label: string
  columnIndex: number
  sortColumn: number | null
  sortDir: 'asc' | 'desc'
  onSort: (columnIndex: number) => void
  align?: 'left' | 'right' | 'center'
  className?: string
}

export function GenericSortHeader({
  label,
  columnIndex,
  sortColumn,
  sortDir,
  onSort,
  align = 'left',
  className,
}: GenericSortHeaderProps) {
  return (
    <th
      className={clsx(
        'deal-table-sort-th cursor-pointer select-none transition-colors',
        sortColumn === columnIndex ? 'text-amber-300' : 'text-stone-600 hover:text-stone-400',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
      onClick={() => onSort(columnIndex)}
    >
      <span
        className={clsx(
          'inline-flex items-center gap-0.5',
          align === 'right' && 'justify-end w-full',
          align === 'center' && 'justify-center w-full',
        )}
      >
        {label}
        {sortColumn === columnIndex && (
          <ArrowUpDown size={8} className={clsx('text-amber-400', sortDir === 'asc' && 'rotate-180')} />
        )}
      </span>
    </th>
  )
}

export function useGenericTableSort<T>(
  rows: T[],
  getValue: (row: T, columnIndex: number) => string | number,
  defaultColumn: number | null = null,
) {
  const [sortColumn, setSortColumn] = useState<number | null>(defaultColumn)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const toggleSort = useCallback(
    (columnIndex: number) => {
      if (sortColumn === columnIndex) {
        setSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'))
        return
      }
      setSortColumn(columnIndex)
      setSortDir('asc')
    },
    [sortColumn],
  )

  const sortedRows = useMemo(() => {
    if (sortColumn == null) {
      return rows
    }
    return [...rows].sort((left, right) => {
      const leftValue = getValue(left, sortColumn)
      const rightValue = getValue(right, sortColumn)
      const direction = sortDir === 'asc' ? 1 : -1
      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return direction * (leftValue - rightValue)
      }
      return direction * String(leftValue).localeCompare(String(rightValue))
    })
  }, [getValue, rows, sortColumn, sortDir])

  return { sortColumn, sortDir, toggleSort, sortedRows }
}

/** Whether a row id addresses a real deal record and can open Deal Dossier.
 *
 *  Callers that should never open a surface pass openProfileOnClick={false}.
 */
export function isLoanTableId(id: string) {
  return /^[A-Z]{2,4}-[A-Z0-9-]+$/i.test(id)
}

export { compareLoans, type LoanSortKey }
